import {
  Box,
  Flex,
  FlexProps,
  Heading,
  Icon,
  IconButton,
  LinkBox,
  LinkOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  RecipeHomepageResponse,
  RecipeHomepageTags,
} from '@diamond/shared/types';
import { RecipeTag, showToast } from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useRouteQuery, useRouteRedirection } from '@diamond/sol-buyer/utils';
import {
  RECIPE_QUERY_KEY,
  useIsAuthenticated,
  usePostRecipeFavoriteMutation,
} from '@diamond/sol-buyer-context';
import { FavoriteBorder, FavoriteOutlined } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useCallback } from 'react';

import { Image } from '../common';
import { ViewCount } from './ViewCount';

type RecipeCardProps = FlexProps & {
  recipe: RecipeHomepageResponse['recipes'][number];
  isCompact?: boolean;
  onFavoriteClick?: (recipeId: string) => void;
  handleTrackEvent?: (
    recipe: RecipeHomepageResponse['recipes'][number]
  ) => void;
  section?: string;
};

export function RecipeCard({
  recipe,
  isCompact,
  onFavoriteClick,
  handleTrackEvent,
  section,
  ...rest
}: RecipeCardProps) {
  const imageSize = isCompact ? '128px' : '160px';

  const routeQuery = useRouteQuery();
  const isAuthenticated = useIsAuthenticated();
  const { withAuth } = useRouteRedirection({ isAuthenticated });

  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutate: postRecipeFavorite } = usePostRecipeFavoriteMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RECIPE_QUERY_KEY],
      });
      const isFavorite = recipe?.is_favorite;
      showToast(
        toast,
        'success',
        isFavorite
          ? 'Resep berhasil dihapus dari favorit'
          : 'Resep berhasil ditambahkan ke favorit'
      );
    },
    onError: (errorMessage) => {
      showToast(toast, 'error', errorMessage);
    },
  });

  const handleFavorite = useCallback(
    async (recipeId: string) => {
      await withAuth(
        () => {
          postRecipeFavorite(recipeId);
        },
        'recipes',
        routeQuery?.['recipesId']
      );
      if (!recipe.is_favorite) {
        trackEvent(TRACKING_EVENTS.ADD_TO_FAV_RECIPE, {
          recipe_name: recipe.title,
          section_name: section || TRACKING_PAGES.RECIPE,
        });
      }
    },
    [
      postRecipeFavorite,
      recipe.is_favorite,
      recipe.title,
      routeQuery,
      section,
      withAuth,
    ]
  );

  return (
    <LinkBox
      w="full"
      as={Flex}
      p="4"
      gap="4"
      bg="white"
      justifyContent="space-between"
      rounded="xl"
      mb={!isCompact ? '3' : 'unset'}
      data-recipe-id={recipe.id}
      {...rest}
    >
      <Flex
        flex="1"
        minW="0"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          <LinkOverlay
            as={Link}
            href={`/recipes/${recipe.slug}`}
            onClick={() => handleTrackEvent?.(recipe)}
          >
            <Heading
              fontSize={{ base: 'sm', md: 'md' }}
              mb={{ base: '1', md: '1.5' }}
            >
              {recipe.title}
            </Heading>
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              textTransform="capitalize"
            >
              {Array.isArray(recipe.brands)
                ? recipe.brands.join(' · ').toLowerCase()
                : ''}
            </Text>
          </LinkOverlay>
        </Box>
        <Flex mt="2" alignItems="end" justifyContent="space-between">
          <ViewCount count={recipe.views} />
          <Flex flexWrap="wrap" justifyContent="flex-end" gap="2">
            {recipe.tags.slice(0, 2).map((t: RecipeHomepageTags, i) => (
              <RecipeTag key={i} tag={t} />
            ))}
          </Flex>
        </Flex>
      </Flex>
      <Box
        w={{ base: '104px', md: imageSize }}
        h={{ base: '104px', md: imageSize }}
        flex="none"
        isolation="isolate"
        position="relative"
        borderRadius="base"
      >
        <IconButton
          zIndex="99"
          right="1"
          top="2"
          variant="link"
          position="absolute"
          aria-label="Add to favorite"
          onClick={() => {
            if (onFavoriteClick) {
              onFavoriteClick(recipe.id);
            } else {
              handleFavorite(recipe.id);
            }
          }}
          _hover={{
            color: 'red.500',
          }}
          icon={
            recipe.is_favorite ? (
              <Icon as={FavoriteOutlined} color="red" />
            ) : (
              <Icon as={FavoriteBorder} />
            )
          }
        />
        <Image
          alt="Recipe Illustration"
          src={recipe.thumbnail}
          fill
          style={{ borderRadius: 'inherit', objectFit: 'cover' }}
        />
      </Box>
    </LinkBox>
  );
}
